import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Box, Chip } from "@mui/material";
import MainApi from "../../../../../../services/mainApi.service";
import { IIntegrationRuleMovs } from "../../../../../../models/integration-rule-mov";
import { memo, useMemo, useState } from "react";
import { IntegrationRuleMov } from "./IntegrationRuleMovForm/IntegrationRuleMov";

const INTEGRATION_RULE_MOVS_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/4uBusIntegrationRuleMov/";
const integrationRuleMovService = new MainApi<IIntegrationRuleMovs>(
  INTEGRATION_RULE_MOVS_ROUTE,
);

const translationPath = "page.4uBus.integrationRule.integrationRuleMov.";

interface IIntegrationRuleMovPageProps {
  busAutomationCode?: number | null;
}

export const IntegrationRuleMovPage = memo(
  ({ busAutomationCode }: IIntegrationRuleMovPageProps) => {
  const { id: busIntegrationRuleId } = useParams();

  const { t } = useTranslation();

  const [iServiceForm, setIServiceForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<IIntegrationRuleMovs>[] = [
    {
      field: "individualExternalId",
      headerName:
        t(translationPath + "individual_external_id") ||
        "Individual External Id",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "_4uBusIntegrationRuleMovIProcedClasses",
      headerName:
        t(translationPath + "internal_proc_classification") ||
        "Internal Procedure Classification",
      renderCell: ({ row: { _4uBusIntegrationRuleMovIProcedClasses } }) => {
        if (!_4uBusIntegrationRuleMovIProcedClasses?.length) return "-";
        const text = _4uBusIntegrationRuleMovIProcedClasses
          .map((item) => item.externalName)
          .join("; ");

        return (
          <OverflowTooltip
            text={text}
            tooltip={text}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "_4uBusIntegrationRuleMovProcedTypes",
      headerName: t(translationPath + "procedure_type") || "Procedure Type",
      renderCell: ({ row: { _4uBusIntegrationRuleMovProcedTypes } }) => {
        if (!_4uBusIntegrationRuleMovProcedTypes?.length) return "-";
        const text = _4uBusIntegrationRuleMovProcedTypes
          .map((item) => item.externalName)
          .join("; ");

        return (
          <OverflowTooltip
            text={text}
            tooltip={text}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      renderCell: (params) => {
        return (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={params.value ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    setIServiceForm(true);
  };

  const handleRowClick = (params: GridRowParams<IIntegrationRuleMovs>) => {
    if (!params.row) return;
    setIServiceForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setIServiceForm(false);
  };

  const params = useMemo(() => {
    return {
      _4uBusIntegrationRuleId: busIntegrationRuleId,
    };
  }, [busIntegrationRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {iServiceForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <IntegrationRuleMov
            id={selectedId}
            busIntegrationRuleId={busIntegrationRuleId}
            busAutomationCode={busAutomationCode}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!iServiceForm && (
        <Box height={600}>
          <Table<IIntegrationRuleMovs>
            showDefaultMacroFilters={false}
            service={integrationRuleMovService}
            serviceProps={params}
            columns={columns}
            title={t(translationPath + "integration_rule_movs")}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputPlaceHolder={t(translationPath + "search")}
            addButtonLabel={
              t(translationPath + "add_integration_rule_mov") || "Add Move"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
});
